import { UserX, Check } from "lucide-react";
import type { AdminUser } from "@/components/admin/_mockData";

/**
 * Stark confirm dialog for disabling a user. Requires typing the user's
 * email to enable the destructive action. ROADS-476 deliverable A2.
 */
interface DisableUserModalProps {
  user: AdminUser;
  open: boolean;
  onCancel: () => void;
  onConfirm?: (reason: string) => void;
}

export default function DisableUserModal({
  user,
  open,
  onCancel,
}: DisableUserModalProps) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="disable-user-modal-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-6"
      data-testid="admin-disable-user-modal"
    >
      <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start gap-3.5 border-b border-red-200 bg-red-50 px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-600">
            <UserX aria-hidden="true" className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2
              id="disable-user-modal-title"
              className="text-lg font-bold text-red-800"
            >
              Disable user
            </h2>
            <p className="text-xs font-medium text-red-800">
              Immediately revokes all sessions. Reversible via Enable user.
            </p>
          </div>
        </div>
        <div className="space-y-4 p-5">
          <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3.5">
            <div
              aria-hidden="true"
              className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white"
              style={{ backgroundColor: user.avatarHex }}
            >
              {user.initials}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-900">
                {user.name}
              </div>
              <div className="truncate text-[11px] text-slate-500">
                {user.email} · {user.id.slice(0, 12)}…{user.id.slice(-5)}
              </div>
            </div>
          </div>
          <section>
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              What happens
            </h3>
            <ul className="mt-2 space-y-2 text-xs leading-relaxed text-slate-700">
              {[
                "WorkOS user marked disabled — login blocked",
                "All session:* Redis keys flushed within 60s",
                "Active subscriptions remain (cancel separately if needed)",
                "Action audit-logged as Bharat C. with reason text",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <Check
                    aria-hidden="true"
                    className="mt-0.5 w-3.5 h-3.5 flex-shrink-0 text-red-600"
                  />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </section>
          <div>
            <label
              htmlFor="disable-confirm-email"
              className="block text-[11px] font-semibold text-slate-700"
            >
              Type the user&apos;s email to confirm
            </label>
            <div className="mt-1.5 flex items-center gap-2 rounded-lg border-2 border-red-500 bg-white px-3.5 py-3">
              <input
                id="disable-confirm-email"
                type="text"
                defaultValue={user.email}
                className="flex-1 bg-transparent font-mono text-sm font-medium text-slate-900 focus:outline-none"
              />
              <Check
                aria-hidden="true"
                className="w-3.5 h-3.5 text-emerald-600"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="disable-reason"
              className="block text-[11px] font-semibold text-slate-700"
            >
              Reason (audit log)
            </label>
            <textarea
              id="disable-reason"
              className="mt-1.5 w-full rounded-lg border border-slate-200 p-3 text-xs text-slate-900"
              defaultValue="Suspicious automation pattern — investigating before re-enable."
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2.5 border-t border-slate-200 bg-slate-50 px-5 py-3.5">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-700"
          >
            <UserX aria-hidden="true" className="w-3.5 h-3.5" />
            Disable user
          </button>
        </div>
      </div>
    </div>
  );
}
